import React, { useState, useMemo, useEffect } from 'react'
import './ConversationPopup.css'
import ReactMarkdown from 'react-markdown'
import * as XLSX from 'xlsx'

const endpoints = {
  'bot.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-insights.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-insights.svc.stratsys.com',
  'localhost:8080': 'http://localhost:5001'
}

// ─────────────────────────────────────────────────────────────────────────────
// RoutePopup – glass‑morphic side card
// ─────────────────────────────────────────────────────────────────────────────
function RoutePopup({ route, message_id, onClose }) {
  const [agentDetails, setAgentDetails] = useState({})
  // Build a linear path from "init" to "last_node", guarding against loops
  const { nodes, valid } = useMemo(() => {
    if (!route || !route.init) return { nodes: [], valid: false }

    const path = [] // ordered list of the nodes we walk
    const visited = new Set() // tracks keys we’ve already added
    let currentKey = 'init'

    const resolveDuplicate = (base) => {
      if (!visited.has(base)) return base // base hasn’t been used yet

      let i = 1
      while (true) {
        const candidate = `${base}-${i}`
        if (!route[candidate]) return null // suffix doesn’t exist → corrupt reference
        if (!visited.has(candidate)) return candidate
        i += 1 // keep trying the next suffix
      }
    }

    // 🚶‍♂️ Traverse the graph
    while (currentKey) {
      const current = route[currentKey]
      if (!current) break // missing entry → invalid graph

      path.push({ key: currentKey, ...current })
      visited.add(currentKey)

      // Happy-path exit at the terminator
      if (current.next === 'last_node') {
        path.push({ key: 'last_node', next: null, details: {} })
        return { nodes: path, valid: true }
      }

      // Move to the next (handling possible revisits)
      currentKey = resolveDuplicate(current.next)
    }

    // Anything that falls through here is invalid
    return { nodes: path, valid: false }
  }, [route])

  // Fetch agent details for all agent_chat nodes
  useEffect(() => {
    console.log('Message ID: ', message_id)
    const fetchAgentDetails = async () => {
      console.log('Fetching agent details...')
      console.log(nodes)
      let updates = {}
      await Promise.all(
        nodes
          .filter((n) => n.key === 'agent_chat')
          .map(async (n) => {
            try {
              const res = await window.PDR.api.get(
                `${endpoints[window.location.host]}/get_agent_calls?id=${encodeURIComponent(message_id)}`
              )
              const data = res.map((obj) => ({
                name: obj.name,
                input_args: obj.input_args,
                response: obj.response,
                start_time: obj.start_time,
                call_duration: obj.duration,
                status: obj.status,
                call_nr: obj.call_nr
              }))
              updates[message_id] = data
              console.log('Agent details fetched:', updates)
            } catch (e) {
              console.error('Error fetching agent details:', e)
            }
          })
      )
      if (Object.keys(updates).length > 0) {
        console.log('Agent details fetched2:', updates)
        setAgentDetails(updates)
      }
    }
    fetchAgentDetails()
  }, [nodes])

  const stopProp = (e) => e.stopPropagation() // prevent overlay close

  return (
    <div className="route-popup" onClick={stopProp}>
      <button className="close-button route-close" onClick={onClose}>
        &times;
      </button>

      <h3 className="shining-text">Route Debugger</h3>

      {valid ? (
        <div className="flow">
          {nodes.map((n, idx) => {
            // Merge agent details if available
            let details = n.details
            return (
              <React.Fragment key={idx}>
                <div className="flow-node">
                  <div className="node-header shining-text">{n.key}</div>
                  {details && Object.keys(details).length > 0 && (
                    <pre className="details">{JSON.stringify(details, null, 2)}</pre>
                  )}
                  {(n.key === 'agent_chat' || n.key.split('-')[0] === 'agent_chat') && agentDetails[message_id] && (
                    <pre className="details">
                      {'Agent call: ' +
                        JSON.stringify(
                          agentDetails[message_id].filter((call) => {
                            const splitKey = n.key.split('-')[1]

                            if (call.call_nr === undefined) {
                              return true
                            }
                            if (splitKey === undefined) {
                              return call.call_nr === 0
                            }
                            return call.call_nr === parseInt(splitKey, 10)
                          }) || '',
                          null,
                          2
                        )}
                    </pre>
                  )}
                </div>
                {/* Visual connector arrow, except after last node */}
                {n.next && (
                  <div className="arrow" aria-hidden="true">
                    &#x2193;
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      ) : (
        <p className="error">Graph couldn’t be visualised for these nodes.</p>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// SourcesPopup – glass‑morphic side card for sources
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// StepCardsPanel – chat_turns trace renderer (replaces RoutePopup for new docs)
// ─────────────────────────────────────────────────────────────────────────────
const STEP_STYLES = {
  reasoning: { color: '#3F8EFC', icon: '🟦', label: 'Reasoning' },
  skill: { color: '#F39C12', icon: '🟧', label: 'Skill' },
  rag: { color: '#8E44AD', icon: '🟪', label: 'RAG' },
  mcp: { color: '#7F5539', icon: '🟫', label: 'MCP' },
  sub_agent: { color: '#F1C40F', icon: '🟨', label: 'Sub-agent' },
  tool: { color: '#3F8EFC', icon: '🟦', label: 'Tool' },
  capability: { color: '#3F8EFC', icon: '🟦', label: 'Capability' },
  approval: { color: '#E74C3C', icon: '🟥', label: 'Approval' },
  final_answer: { color: '#2ECC71', icon: '✅', label: 'Final answer' }
}

function stepStyleFor(step) {
  if (step.kind === 'reasoning') return STEP_STYLES.reasoning
  return STEP_STYLES[step.type] || STEP_STYLES.tool
}

const MAX_INDENT_DEPTH = 5

function StepCard({ step, onSourceClick, depth = 0 }) {
  const [expanded, setExpanded] = useState(false)
  const style = stepStyleFor(step)
  const duration = step.duration_ms != null ? `${step.duration_ms} ms` : ''
  const statusBadge =
    step.status && step.status !== 'ok' ? <span className="step-status-badge">{step.status}</span> : null
  const headerTitle =
    step.kind === 'reasoning'
      ? style.label
      : `${style.label}${step.name ? ' · ' + step.name : ''}`

  // Detect code-like args (e.g. "code", "script", "query")
  const CODE_KEYS = new Set(['code', 'script', 'query', 'sql', 'python_code', 'execute_code'])
  const hasCodeArg = step.args && Object.keys(step.args).some((k) => CODE_KEYS.has(k))

  // Chained MCP calls can nest arbitrarily deep; cap the indent so cards
  // never staircase off the right edge of the panel.
  const indent = Math.min(depth, MAX_INDENT_DEPTH) * 12

  return (
    <div
      className="step-card"
      style={{ borderLeft: `4px solid ${style.color}`, marginLeft: indent }}
      onClick={(e) => {
        e.stopPropagation()
        setExpanded((v) => !v)
      }}>
      <div className="step-card-header">
        <span className="step-icon">{style.icon}</span>
        {depth > MAX_INDENT_DEPTH && <span className="step-depth-badge">L{depth}</span>}
        <span className="step-title">{headerTitle}</span>
        {duration && <span className="step-duration">{duration}</span>}
        {statusBadge}
        <span className="step-expand-icon">{expanded ? '▾' : '▸'}</span>
      </div>
      {step.kind === 'reasoning' && step.text && (
        <div className="step-card-body step-reasoning-text">
          {step.type === 'final_answer' ? (
            <ReactMarkdown>{expanded ? step.text : (step.text.length > 500 ? step.text.slice(0, 500) + '…' : step.text)}</ReactMarkdown>
          ) : (
            <em>{expanded ? step.text : (step.text.length > 300 ? step.text.slice(0, 300) + '…' : step.text)}</em>
          )}
        </div>
      )}
      {step.kind !== 'reasoning' && step.result_summary && !expanded && (
        <div className="step-card-summary">{step.result_summary.length > 120 ? step.result_summary.slice(0, 120) + '…' : step.result_summary}</div>
      )}
      {step.kind !== 'reasoning' && step.args && !expanded && (
        <div className="step-card-summary">
          {(() => {
            const preview = Object.entries(step.args).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ')
            return preview.length > 100 ? preview.slice(0, 100) + '…' : preview
          })()}
        </div>
      )}
      {expanded && (
        <div className="step-card-body step-card-body-expanded" onClick={(e) => e.stopPropagation()}>
          {step.skill_id && (
            <div>
              <strong>skill:</strong> {step.skill_id}
            </div>
          )}
          {step.mcp_server && (
            <div>
              <strong>server:</strong> {step.mcp_server}
            </div>
          )}
          {step.args && Object.keys(step.args).length > 0 && (
            hasCodeArg ? (
              <div className="step-args-section">
                {Object.entries(step.args).map(([key, value]) => (
                  <div key={key} className="step-arg-entry">
                    <div className="step-arg-label">{key}:</div>
                    {CODE_KEYS.has(key) ? (
                      <pre className="details details-code">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
                    ) : (
                      <pre className="details">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <pre className="details">{JSON.stringify(step.args, null, 2)}</pre>
            )
          )}
          {step.result_summary && (
            <div className="step-result-section">
              <div className="step-arg-label">Result:</div>
              <pre className="details">{step.result_summary}</pre>
            </div>
          )}
          {step.error && (
            <pre className="details details-error">
              {typeof step.error === 'string' ? step.error : JSON.stringify(step.error, null, 2)}
            </pre>
          )}
          {Array.isArray(step.retrieval) && step.retrieval.length > 0 && (
            <div className="retrieval-chips">
              {step.retrieval.map((r, i) => (
                <button
                  key={i}
                  className="retrieval-chip"
                  onClick={(e) => {
                    e.stopPropagation()
                    onSourceClick && onSourceClick(step.retrieval)
                  }}
                  title={`${r.index || ''} · score ${r.score ?? ''}`}>
                  {r.title || r.doc_id || `chunk ${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function InitSnapshotSection({ initSnapshot }) {
  const [expanded, setExpanded] = useState(false)
  if (!initSnapshot || (!initSnapshot.feature_flags && !initSnapshot.mcp_status)) return null

  const flags = initSnapshot.feature_flags || {}
  const flagEntries = Object.entries(flags)
  const mcpStatus = initSnapshot.mcp_status || {}
  const servers = mcpStatus.servers || {}
  const serverEntries = Object.entries(servers)

  const onCount = flagEntries.filter(([, v]) => v).length
  const serversUp = serverEntries.filter(([, s]) => s.available).length

  return (
    <div className={`init-snapshot-section ${expanded ? 'is-expanded' : ''}`}>
      <div
        className="init-snapshot-header"
        onClick={(e) => {
          e.stopPropagation()
          setExpanded((v) => !v)
        }}>
        <span className="init-snapshot-title">⚙️ Init snapshot</span>
        <span className="init-snapshot-meta">
          {flagEntries.length > 0 && (
            <span className="init-snapshot-pill">
              flags {onCount}/{flagEntries.length} on
            </span>
          )}
          {serverEntries.length > 0 && (
            <span className="init-snapshot-pill">
              MCP {serversUp}/{serverEntries.length} up
            </span>
          )}
          <span className="step-expand-icon">{expanded ? '▾' : '▸'}</span>
        </span>
      </div>
      {expanded && (
        <div className="init-snapshot-body" onClick={(e) => e.stopPropagation()}>
          <div className="init-snapshot-block">
            <div className="init-snapshot-block-title">
              Feature flags
              {initSnapshot.unleash_client_healthy === false && (
                <span className="step-status-badge">unleash unhealthy</span>
              )}
            </div>
            {flagEntries.length === 0 ? (
              <p className="error">No flags evaluated this turn.</p>
            ) : (
              <div className="init-snapshot-table-wrap">
                <table className="init-snapshot-table">
                  <tbody>
                    {flagEntries.map(([name, value]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td className={value ? 'flag-on' : 'flag-off'}>{value ? 'ON' : 'OFF'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="init-snapshot-block">
            <div className="init-snapshot-block-title">
              MCP servers (use_registry: {mcpStatus.use_registry ? 'true' : 'false'})
            </div>
            {serverEntries.length === 0 ? (
              <p className="error">No MCP servers reported (mcp-status empty or registry disabled).</p>
            ) : (
              <div className="init-snapshot-table-wrap">
                <table className="init-snapshot-table">
                  <thead>
                    <tr>
                      <th>Server</th>
                      <th>Enabled</th>
                      <th>Flag</th>
                      <th>Available</th>
                      <th>Connection</th>
                      <th>Tools</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serverEntries.map(([id, s]) => (
                      <tr key={id}>
                        <td>{s.name || id}</td>
                        <td className={s.enabled ? 'flag-on' : 'flag-off'}>{s.enabled ? 'yes' : 'no'}</td>
                        <td className={s.flag_enabled ? 'flag-on' : 'flag-off'}>{s.flag_enabled ? 'yes' : 'no'}</td>
                        <td className={s.available ? 'flag-on' : 'flag-off'}>{s.available ? 'yes' : 'no'}</td>
                        <td
                          title={s.connection_error || ''}
                          className={s.connection_status === 'error' ? 'flag-off' : ''}>
                          {s.connection_status}
                        </td>
                        <td>{s.tool_count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function StepCardsPanel({ trace, onClose, onSourceClick }) {
  const { attemptGroups, childrenByParent } = useMemo(() => {
    const steps = (trace && trace.steps) || []
    const map = {}
    const groups = {}
    for (const s of steps) {
      const attempt = s.attempt ?? 0
      if (s.parent_step_id) {
        ;(map[s.parent_step_id] = map[s.parent_step_id] || []).push(s)
      } else {
        ;(groups[attempt] = groups[attempt] || []).push(s)
      }
    }
    // Convert to sorted array of [attemptNum, steps[]]
    const sorted = Object.entries(groups)
      .map(([k, v]) => [Number(k), v])
      .sort((a, b) => a[0] - b[0])
    return { attemptGroups: sorted, childrenByParent: map }
  }, [trace])

  const renderTree = (step, depth) => (
    <React.Fragment key={step.id || `${step.kind}-${step.started_at}`}>
      <StepCard step={step} depth={depth} onSourceClick={onSourceClick} />
      {(childrenByParent[step.id] || []).map((child) => renderTree(child, depth + 1))}
    </React.Fragment>
  )

  const classification = (trace && trace.classification) || {}
  const timings = (trace && trace.timings) || {}
  const metrics = (trace && trace.metrics) || {}

  return (
    <div className="route-popup trace-panel" onClick={(e) => e.stopPropagation()}>
      <button className="close-button route-close" onClick={onClose}>
        &times;
      </button>
      <div className="trace-panel-header">
        <h3 className="shining-text">Turn Trace</h3>
        <div className="trace-summary">
          <div>
            <strong>Path:</strong> {trace?.orchestrator_path || '—'} · <strong>Total:</strong>{' '}
            {timings.total_ms != null ? `${timings.total_ms} ms` : '—'} · <strong>Thinking:</strong>{' '}
            {timings.llm_thinking_ms_total != null ? `${timings.llm_thinking_ms_total} ms` : '—'}
          </div>
          {(classification.skills_activated || []).length > 0 && (
            <div>
              <strong>Skills:</strong> {classification.skills_activated.join(', ')}
            </div>
          )}
          {(classification.tool_names_used || []).length > 0 && (
            <div>
              <strong>Tools:</strong> {classification.tool_names_used.join(', ')}
            </div>
          )}
          {(metrics['Answer Relevancy'] || metrics['Faithfulness']) && (
            <div>
              {metrics['Answer Relevancy'] && (
                <span>
                  <strong>AR:</strong> {metrics['Answer Relevancy'].score?.toFixed?.(2)} ·{' '}
                </span>
              )}
              {metrics['Faithfulness'] && (
                <span>
                  <strong>F:</strong> {metrics['Faithfulness'].score?.toFixed?.(2)}
                </span>
              )}
            </div>
          )}
        </div>
        <InitSnapshotSection initSnapshot={trace && trace.init_snapshot} />
      </div>
      <div className="flow trace-steps">
        {attemptGroups.length === 0 && <p className="error">No steps recorded for this turn.</p>}
        {attemptGroups.map(([attempt, steps]) => (
          <React.Fragment key={attempt}>
            {attemptGroups.length > 1 && (
              <div className={`attempt-separator ${attempt > 0 ? 'attempt-retry' : ''}`}>
                {attempt === 0 ? '① Initial attempt' : `⟳ Retry attempt ${attempt + 1}`}
                {attempt > 0 && <span className="attempt-failed-badge">failed</span>}
              </div>
            )}
            {steps.map((s) => renderTree(s, 0))}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

function SourcesPopup({ sourcesData, onClose }) {
  return (
    <div className="sources-popup" onClick={(e) => e.stopPropagation()}>
      <button className="close-button sources-close" onClick={onClose}>
        &times;
      </button>
      <div className="sources-header">
        <h3 className="shining-text">Sources</h3>
      </div>

      {sourcesData && sourcesData.length > 0 ? (
        sourcesData.map((doc, idx) => (
          <div className="flow-node" key={idx}>
            <div className="node-header">
              {doc.index}: {doc.title}
            </div>
            <pre className="details">{doc.content}</pre>
          </div>
        ))
      ) : (
        <p className="error">No sources found.</p>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ConversationPopup – main chat view
// ─────────────────────────────────────────────────────────────────────────────
const ConversationPopup = ({ conversation, onClose, metadata }) => {
  const [selectedRoute, setSelectedRoute] = useState(null)
  const [selectedTrace, setSelectedTrace] = useState(null)
  const [selectedMessageId, setSelectedMessageId] = useState(null)
  const [sourcesPopupOpen, setSourcesPopupOpen] = useState(false)
  const [sourcesData, setSourcesData] = useState([])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedRoute(null)
      setSelectedTrace(null)
      onClose()
    }
  }

  const handleConversationExport = () => {
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(
      conversation.map((msg) => ({
        timestamp: msg.timestamp,
        type: msg.type,
        feedback: msg.feedback,
        sources: formatSources(msg.sources),
        message: msg.message,
        route: JSON.stringify(msg.route)
      }))
    )
    XLSX.utils.book_append_sheet(wb, ws, 'Conversation')
    XLSX.writeFile(wb, `Conversation_${metadata.tenant_id}.xlsx`)
  }

  const formatSources = (sources) => {
    if (!sources) return ''
    return sources.map((s) => `${s.title} (Chunk ${s.id})`).join(', ')
  }

  const handleFetchSources = async (srcList, idxList) => {
    try {
      const res = await window.PDR.api.post(`${endpoints[window.location.host]}/fetch_sources_content`, {
        sources: srcList || [],
        indexes: idxList || []
      })
      setSourcesData(res)
      setSourcesPopupOpen(true)
    } catch (e) {
      console.error('Error fetching sources content:', e)
    }
  }

  const handleRetrievalChunks = (chunks) => {
    const mapped = (chunks || []).map((c) => ({
      index: c.index || '',
      title: c.title || c.doc_id || 'chunk',
      content: c.chunk || c.content || ''
    }))
    setSourcesData(mapped)
    setSourcesPopupOpen(true)
  }

  const handleClick = (msg) => {
    if (msg.type !== 'ai') return
    setSelectedMessageId(msg.id || null)
    if (msg.trace) {
      setSelectedTrace(msg.trace)
      setSelectedRoute(null)
      // Preload sources panel from the trace's RAG retrievals (if any).
      const allRetrieval = (msg.trace.steps || [])
        .filter((s) => s.type === 'rag' && Array.isArray(s.retrieval))
        .flatMap((s) => s.retrieval)
      // Also check trace.answer.sources (persisted from the chatbot response)
      const traceSources = (msg.trace.answer && msg.trace.answer.sources) || []
      if (allRetrieval.length > 0) {
        handleRetrievalChunks(allRetrieval)
      } else if (traceSources.length > 0) {
        const mapped = traceSources.map((s) => ({
          index: s.index || '',
          title: s.title || s.id || 'source',
          content: s.content || s.chunk || ''
        }))
        setSourcesData(mapped)
        setSourcesPopupOpen(true)
      } else if ((msg.sources || []).length > 0) {
        // Sources stored directly on the message (from sessionHistory)
        const mapped = msg.sources.map((s) => ({
          index: s.index || '',
          title: s.title || s.id || 'source',
          content: s.content || s.chunk || ''
        }))
        setSourcesData(mapped)
        setSourcesPopupOpen(true)
      } else if ((msg.selected_sources || []).length || (msg.used_indexes || []).length) {
        handleFetchSources(msg.selected_sources, msg.used_indexes)
      } else {
        // No sources available — close sources panel
        setSourcesPopupOpen(false)
      }
    } else {
      setSelectedRoute(msg.route || null)
      setSelectedTrace(null)
      if ((msg.sources || []).length > 0) {
        const mapped = msg.sources.map((s) => ({
          index: s.index || '',
          title: s.title || s.id || 'source',
          content: s.content || s.chunk || ''
        }))
        setSourcesData(mapped)
        setSourcesPopupOpen(true)
      } else {
        handleFetchSources(msg.selected_sources, msg.used_indexes)
      }
    }
  }

  return (
    <div className="conversation-popup-overlay" onClick={handleOverlayClick}>
      <div className="popup-wrapper">
        {/* ── Glass morphic sources popup ─────────────────────────────────── */}
        {sourcesPopupOpen && (
          <SourcesPopup
            sourcesData={sourcesData}
            onClose={() => setSourcesPopupOpen(false)}
          />
        )}
        {/* ── Main chat popup ─────────────────────────────────────────────── */}
        <div className={`conversation-popup ${selectedRoute || selectedTrace ? 'route-open' : ''}`}>
          <one-ux-button label="Export to Excel" onClick={handleConversationExport}>
            <one-ux-icon icon="export" />
          </one-ux-button>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>

          <h2>{`Full conversation with ${metadata.tenant_id}`}</h2>

          <div className="chat-container">
            {conversation.map((msg, index) => {
              const isHuman = msg.type === 'human'
              let feedbackClass = 'neutral'
              if (msg.feedback === 1) feedbackClass = 'positive'
              else if (msg.feedback === -1) feedbackClass = 'negative'

              return (
                <div key={index} className={`message-row ${isHuman ? 'human-row' : 'ai-row'}`}>
                  <div
                    className={`message-bubble ${isHuman ? 'human-bubble' : 'ai-bubble'}`}
                    onClick={() => handleClick(msg)}
                    role={!isHuman ? 'button' : undefined}
                    tabIndex={!isHuman ? 0 : undefined}>
                    <span className={`feedback-dot ${feedbackClass}`} />
                    <ReactMarkdown>{msg.message}</ReactMarkdown>
                    <div className="timestamp">{msg.timestamp}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Trace / route side panel ────────────────────────────────────── */}
        {selectedTrace && (
          <StepCardsPanel
            trace={selectedTrace}
            message_id={selectedMessageId}
            onClose={() => setSelectedTrace(null)}
            onSourceClick={handleRetrievalChunks}
          />
        )}
        {selectedRoute && !selectedTrace && (
          <RoutePopup route={selectedRoute} message_id={selectedMessageId} onClose={() => setSelectedRoute(null)} />
        )}
      </div>
    </div>
  )
}

export default ConversationPopup
